/**
 * PostcardMyPage — /postcard/my
 * Shows all postcards created by the logged-in user.
 * Requires auth — redirects to /login if not logged in.
 */
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getUserInfo } from '@shared/api/client';
import PostcardCanvas from '../components/PostcardCanvas';
import { ROUTES } from '@app/router/routes';
import { useLanguage } from '@shared/contexts/LanguageContext';

export default function PostcardMyPage() {
  const navigate = useNavigate();
  const user = getUserInfo();
  const { t } = useLanguage();

  const [cards] = useState([]);
  const [loading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, []);

  function formatDate(iso) {
    return new Date(iso).toLocaleDateString('zh-TW', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  }

  return (
    <>
      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F5EFE6] to-[#FAF7F3] border-b border-[#E8DCC8] px-4 py-8">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.3em] text-[#B39029] mb-1 uppercase">My Postcards</p>
            <h1 className="text-2xl font-bold text-[#3E362E]">{t('postcard-my.pageTitle')}</h1>
            {!loading && cards.length > 0 && (
              <p className="text-sm text-[#6E625B] mt-1">{t('postcard-my.totalCount').replace('{{count}}', cards.length)}</p>
            )}
          </div>
          <Link
            to={ROUTES.POSTCARD_CREATE}
            className="flex items-center gap-1.5 bg-[#907654] hover:bg-[#7a6346] text-white rounded-xl px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors shadow-sm"
          >
            <span className="material-symbols-outlined text-base">add</span>
            {t('postcard-my.add')}
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#907654] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-[#6E625B]">{t('postcard-my.loading')}</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && cards.length === 0 && (
          <div className="flex flex-col items-center py-20 gap-5 text-center">
            <div className="w-24 h-24 rounded-full bg-[#F5EFE6] flex items-center justify-center">
              <span className="material-symbols-outlined text-5xl text-[#907654] opacity-60">mail</span>
            </div>
            <div>
              <p className="font-medium text-[#3E362E] mb-1">{t('postcard-my.emptyTitle')}</p>
              <p className="text-sm text-[#6E625B]">{t('postcard-my.emptyHint')}</p>
            </div>
            <Link
              to={ROUTES.POSTCARD_CREATE}
              className="bg-[#907654] hover:bg-[#7a6346] text-white rounded-xl px-8 py-3 text-sm font-medium cursor-pointer transition-colors shadow-sm"
            >
              {t('postcard-my.createFirst')}
            </Link>
          </div>
        )}

        {/* Card list */}
        {!loading && cards.length > 0 && (
          <div className="space-y-3">
            {cards.map((card, idx) => (
              <Link
                key={card.share_code ?? card.record_id}
                to={ROUTES.POSTCARD_VIEW(card.share_code ?? card.record_id)}
                className="block bg-white rounded-2xl p-4 shadow-sm border border-[#F0EAE4] hover:border-[#E8DCC8] hover:shadow-md transition-all cursor-pointer group"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="flex gap-4 items-center">
                  {/* Mini preview */}
                  <div className="shrink-0 w-[100px]">
                    <PostcardCanvas
                      item={{ desktop_url: card.desktop_url, mobile_url: card.mobile_url, is_full_bleed: card.is_full_bleed, name: card.name }}
                      fromName={card.from_name}
                      toName={card.to_name}
                      content={card.content}
                      isMobile={false}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] text-[#B39029] font-medium uppercase tracking-wide">{t('postcard-my.toLabel')}</span>
                      <p className="font-bold text-[#3E362E] truncate">{card.to_name}</p>
                    </div>
                    <p className="text-sm text-[#6E625B] line-clamp-2 leading-relaxed">{card.content}</p>
                    {card.created_at && (
                      <p className="text-xs text-[#6E625B] opacity-40 mt-2 flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">calendar_today</span>
                        {formatDate(card.created_at)}
                      </p>
                    )}
                  </div>

                  {/* Arrow */}
                  <span className="material-symbols-outlined text-[#E8DCC8] group-hover:text-[#907654] transition-colors shrink-0">
                    chevron_right
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
